import React, { useState, useEffect } from 'react'
import { get } from 'lodash'
import { useHistory, useParams } from 'react-router-dom'
import { List, Button, IconDelete, ConfirmationPopup, Notification } from 'shared/components'
import { sendErrorReport, displayValue } from 'shared/helpers'
import { getCustomerAccUserGroups, removeUserGroupFromCustomerAccount } from 'src/customer/actions'
import AddUserToUserGroup from 'src/customer/components/AddUserToUserGroup'

export default function UserGroups() {
  const [showAddUserToUserGroup, setShowAddUserToUserGroup] = useState(false)
  const [isDeleteUserPopupDisplayed, setIsDeleteUserPopupDisplayed] = useState(false)
  const [groupToDelete, setGroupToDelete] = useState(null)
  const [deletingUser, setDeletingUser] = useState(false)
  const { accountId, companyId } = useParams()
  const history = useHistory()
  const [list, setList] = useState([])
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(true)

  const updateGroups = page => {
    setLoading(true)
    getCustomerAccUserGroups(companyId, accountId, page)
      .then(res => {
        setList(get(res, 'data'))
        setLoading(false)
      })
      .catch(err => {
        sendErrorReport(err, 'Cannot get customer licenses')
        setLoading(false)
      })
  }

  useEffect(() => {
    updateGroups(page)
  }, [page])

  const handleUserPageChange = page => {
    setPage(page)
    updateGroups(page)
  }

  const redirectToUserGroupPage = rowData => {
    const userGroupID = get(rowData, 'original.id')
    history.push(`/${companyId}/license_users/user_groups/${userGroupID}`)
  }

  const handleUserDelete = user => {
    setDeletingUser(true)
    removeUserGroupFromCustomerAccount(companyId, groupToDelete.id)
      .then(() => {
        setIsDeleteUserPopupDisplayed(false)
        setGroupToDelete(null)
        updateGroups(page)
        Notification('success', 'User group removed from customer account successfully')
      })
      .catch(err => {
        sendErrorReport(err, 'Cannot delete user from customer account')
        setIsDeleteUserPopupDisplayed(false)
        setGroupToDelete(null)
        Notification('error', 'Failed to remove user from customer account')
      })
      .finally(() => {
        setDeletingUser(false)
      })
  }

  return (
    <>
      <div className='button-wrapper'>
        <Button onClick={() => setShowAddUserToUserGroup(true)}>Add user group</Button>
      </div>
      <List
        clickable
        data={get(list, 'results') || []}
        loading={loading}
        manual
        page={page}
        pages={Math.ceil(get(list, 'count') / 20)}
        minRows={2}
        showPagination
        onPageChange={handleUserPageChange}
        handleClick={rowData => redirectToUserGroupPage(rowData)}
        columns={[
          {
            accessor: 'name',
            Header: __('Name'),
            Cell: cellInfo => displayValue(cellInfo.value)
          },
          {
            accessor: 'code',
            Header: __('Code'),
            Cell: cellInfo => displayValue(cellInfo.value)
          },
          {
            accessor: 'description',
            Header: __('Description'),
            Cell: cellInfo => displayValue(cellInfo.value)
          },
          {
            accessor: 'metadata',
            Header: __('Metadata'),
            Cell: cellInfo => displayValue(JSON.stringify(cellInfo.value))
          },
          {
            className: 'text-center',
            id: 'delete',
            sortable: false,
            Cell: rowData => (
              <Button
                className='edit-button'
                onClick={e => {
                  e.stopPropagation()
                  e.preventDefault()
                  setGroupToDelete(rowData.original)
                  setIsDeleteUserPopupDisplayed(true)
                }}
                type='button'
              >
                <IconDelete height='16px' width='16px' color='#ee5253' />
              </Button>
            )
          }
        ]}
      />
      {showAddUserToUserGroup && (
        <AddUserToUserGroup
          closeCb={() => setShowAddUserToUserGroup(false)}
          confirmCb={() => {
            setShowAddUserToUserGroup(false)
            updateGroups(page)
          }}
        />
      )}
      {isDeleteUserPopupDisplayed && (
        <ConfirmationPopup
          closeCb={() => {
            setIsDeleteUserPopupDisplayed(false)
            setGroupToDelete(null)
          }}
          confirmCb={() => handleUserDelete(groupToDelete)}
          title={`${__('Are you sure you want to remove the user from this group?')}`}
          confirmText={__('Delete')}
          theme='error'
          disabled={deletingUser}
        >
          <div>
            <strong>{get(groupToDelete, 'name')}</strong>
          </div>
        </ConfirmationPopup>
      )}
    </>
  )
}
